import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium tracking-wide transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 select-none active:translate-y-[1px] active:shadow-none',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-[0_2px_0_0_hsl(var(--primary-deep))] hover:bg-primary-light',
        destructive:
          'bg-primary text-primary-foreground shadow-[0_2px_0_0_hsl(var(--primary-deep))] hover:bg-primary-light',
        outline:
          'border border-border bg-card text-foreground shadow-[0_1px_0_0_hsl(var(--border))] hover:bg-accent',
        secondary:
          'bg-secondary text-secondary-foreground shadow-[0_2px_0_0_hsl(var(--border))] hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline px-0 h-auto',
        success:
          'bg-success text-success-foreground shadow-[0_2px_0_0_hsl(var(--success)/0.6)] hover:opacity-90',
      },
      size: {
        default: 'h-11 px-5 text-sm',
        sm: 'h-9 rounded-full px-4 text-sm',
        lg: 'h-12 rounded-full px-7 text-[15px]',
        xl: 'h-14 rounded-full px-9 text-base',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export { buttonVariants };
